import {
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import apiClient from '../client';
import {
  CityView,
  CountryView,
  RegionView,
  ApiError,
  ApiPlatformCollectionResponse,
  extractCollectionItems,
} from '../types';

export const locationKeys = {
  cities: ['cities'] as const,
  citiesList: () => [...locationKeys.cities, 'list'] as const,
  city: (id: number) => [...locationKeys.cities, 'detail', id] as const,
  
  countries: ['countries'] as const,
  countriesList: () => [...locationKeys.countries, 'list'] as const,
  country: (id: number) => [...locationKeys.countries, 'detail', id] as const,
  
  regions: ['regions'] as const,
  regionsList: () => [...locationKeys.regions, 'list'] as const,
  region: (id: number) => [...locationKeys.regions, 'detail', id] as const,
};

export const useCities = (
  page: number = 1,
  options?: Omit<UseQueryOptions<CityView[], ApiError>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<CityView[], ApiError>({
    queryKey: [...locationKeys.citiesList(), page],
    queryFn: async () => {
      const response = await apiClient.get<ApiPlatformCollectionResponse<CityView>>('/cities', { page });
      return extractCollectionItems(response.data);
    },
    ...options,
  });
};

export const useCity = (
  id: number,
  options?: Omit<UseQueryOptions<CityView, ApiError>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<CityView, ApiError>({
    queryKey: locationKeys.city(id),
    queryFn: async () => {
      const response = await apiClient.get<CityView>(`/cities/${id}`);
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useCountries = (
  options?: Omit<UseQueryOptions<CountryView[], ApiError>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<CountryView[], ApiError>({
    queryKey: locationKeys.countriesList(),
    queryFn: async () => {
      const response = await apiClient.get<ApiPlatformCollectionResponse<CountryView>>('/countries');
      return extractCollectionItems(response.data);
    },
    ...options,
  });
};

export const useCountry = (
  id: number,
  options?: Omit<UseQueryOptions<CountryView, ApiError>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<CountryView, ApiError>({
    queryKey: locationKeys.country(id),
    queryFn: async () => {
      const response = await apiClient.get<CountryView>(`/countries/${id}`);
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useRegions = (
  options?: Omit<UseQueryOptions<RegionView[], ApiError>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<RegionView[], ApiError>({
    queryKey: locationKeys.regionsList(),
    queryFn: async () => {
      const response = await apiClient.get<ApiPlatformCollectionResponse<RegionView>>('/regions');
      return extractCollectionItems(response.data);
    },
    ...options,
  });
};

export const useRegion = (
  id: number,
  options?: Omit<UseQueryOptions<RegionView, ApiError>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<RegionView, ApiError>({
    queryKey: locationKeys.region(id),
    queryFn: async () => {
      const response = await apiClient.get<RegionView>(`/regions/${id}`);
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};



